import $ from "jquery";
import _ from "underscore";
import Backbone from "backbone";
import Session from "Session";

const PREFIX = "_feature_flag_";
const ON = "ON";
const OFF = "OFF";
const NOT_CONFIGURED = "NOT_CONFIGURED";

const PropertyModel = Backbone.Model.extend({
    idAttribute: "name",
    sync(method, model, options) {
        if (method === "update") {
            options.attrs = _.pick(this.attributes, "value");
        }
        return Backbone.sync.call(this, method, model, options);
    },

    parse(response) {
        if (response.name) {
            this._id = response.name;
        }
        return response;
    },

    isNew() {
        if (this._id) {
            return false;
        }
        return true;
    },
});

const ClientPropertyCollection = Backbone.Collection.extend({
    url: "api:///v2/properties",
    model: PropertyModel,
});

const DefaultPropertyCollection = Backbone.Collection.extend({
    url: "api:///v2/default/properties",
    model: PropertyModel,
});

const ClientFlagCollection = ClientPropertyCollection.extend({
    initialize(models, parameters) {
        this.name = parameters.name;
    },
    parse(response) {
        const self = this;
        return _.filter(response, (item) => item.name === self.name);
    },
});

const GlobalFlagCollection = DefaultPropertyCollection.extend({
    initialize(models, parameters) {
        this.name = parameters.name;
    },
    parse(response) {
        const self = this;
        return _.filter(response, (item) => item.name === self.name);
    },
});

const BROWSER_FLAGS = "BROWSER_FLAGS";

const BrowserPropertyModel = Backbone.Model.extend({
    save() {
        const deferred = $.Deferred();
        const raw = window.localStorage.getItem(BROWSER_FLAGS);
        const parsed = raw ? JSON.parse(raw) : [];
        const name = this.get("name");
        const existing = _.findWhere(parsed, {
            name,
        });
        const model = this;
        if (existing) {
            _.extend(existing, model.toJSON());
            window.localStorage.setItem(BROWSER_FLAGS, JSON.stringify(parsed));
            model.trigger("sync");
            deferred.resolve();
        } else {
            setTimeout(() => {
                parsed.push(model.toJSON());
                window.localStorage.setItem(
                    BROWSER_FLAGS,
                    JSON.stringify(parsed)
                );
                model.trigger("sync");
                deferred.resolve();
            }, 0);
        }
        return deferred;
    },
    destroy() {
        const deferred = $.Deferred();
        const raw = window.localStorage.getItem(BROWSER_FLAGS);
        const parsed = raw ? JSON.parse(raw) : [];
        const name = this.get("name");
        const existing = _.findWhere(parsed, {
            name,
        });
        const model = this;
        if (existing) {
            setTimeout(() => {
                const without = _.filter(parsed, (item) => item !== existing);
                window.localStorage.setItem(
                    BROWSER_FLAGS,
                    JSON.stringify(without)
                );
                model.trigger("destroy", model);
                deferred.resolve();
            }, 0);
        }
        return deferred;
    },
});

export const BrowserFlagCollection = Backbone.Collection.extend({
    initialize(models, parameters) {
        this.name = parameters.name;
    },
    model: BrowserPropertyModel,
    parse(response) {
        const self = this;
        return _.filter(response, (item) => item.name === self.name);
    },
    _fetchSync() {
        const raw = window.localStorage.getItem(BROWSER_FLAGS);
        const response = raw ? JSON.parse(raw) : [];
        const collection = this;
        collection.set(response, {
            parse: true,
        });
        collection.trigger("sync");
    },
    fetch() {
        const deferred = $.Deferred();
        const raw = window.localStorage.getItem(BROWSER_FLAGS);
        const response = raw ? JSON.parse(raw) : [];
        const collection = this;
        setTimeout(() => {
            collection.set(response, {
                parse: true,
            });
            collection.trigger("sync");
            deferred.resolve();
        }, 0);
        return deferred;
    },

    create(attributes) {
        const collection = this;
        const model = new BrowserPropertyModel(attributes);
        $.when(model.save()).done(() => {
            collection.add(model);
            collection.trigger("sync");
        });
    },
});

function relay(event, source, target) {
    source.on(event, () => {
        target.trigger(event);
    });
}

const FeatureFlagModel = Backbone.Model.extend({
    initialize() {
        this.clientFlags = new ClientFlagCollection([], {
            name: this.get("name"),
        });
        this.globalFlags = new GlobalFlagCollection([], {
            name: this.get("name"),
        });
        this.browserFlags = new BrowserFlagCollection([], {
            name: this.get("name"),
        });

        relay("sync", this.globalFlags, this);
        relay("sync", this.clientFlags, this);
        relay("sync", this.browserFlags, this);

        relay("destroy", this.globalFlags, this);
        relay("destroy", this.clientFlags, this);
        relay("destroy", this.browserFlags, this);
    },

    toJSON() {
        return {
            name: this.get("name"),
            display_name: this.get("displayName"),
            description: this.get("description"),
            state_global: this.getState("GLOBAL"),
            state_client: this.getState("CLIENT"),
            state_browser: this.getState("BROWSER"),
        };
    },

    bootstrap() {
        const deferred = $.Deferred();
        $.when(
            this.clientFlags.fetch(),
            this.globalFlags.fetch(),
            this.browserFlags.fetch()
        ).done(deferred.resolve);
        return deferred;
    },

    getCollectionByLevel(level) {
        switch (level) {
            case "GLOBAL":
                return this.globalFlags;
            case "CLIENT":
                return this.clientFlags;
            case "BROWSER":
                return this.browserFlags;
            default:
                throw new Error(`Level not supported: ${level}`);
        }
    },

    getState(level) {
        const flags = this.getCollectionByLevel(level);
        if (flags.isEmpty()) {
            return NOT_CONFIGURED;
        }
        const flag = flags.first();
        const value = flag.get("value");
        if (value === "ON") {
            return ON;
        }
        return OFF;
    },

    turnOn(level) {
        const collection = this.getCollectionByLevel(level);
        if (collection.isEmpty()) {
            collection.create({
                name: this.get("name"),
                value: ON,
            });
        } else {
            const model = collection.first();
            model.set("value", ON);
            model.save();
        }
    },

    turnOff(level) {
        const collection = this.getCollectionByLevel(level);
        if (collection.isEmpty()) {
            collection.create({
                name: this.get("name"),
                value: OFF,
            });
        } else {
            const model = collection.first();
            model.set("value", OFF);
            model.save();
        }
    },

    removeConfiguration(level) {
        const collection = this.getCollectionByLevel(level);
        const model = collection.first();
        if (model) {
            model.destroy();
        }
    },

    isOn() {
        const name = this.get("name");
        let value;

        value = Session.getInstance().getProperties()[name];

        this.browserFlags._fetchSync();
        const flag = this.browserFlags.first();
        if (flag) {
            value = flag.get("value");
        }

        return value === "ON";
    },
});

const FeatureFlagCollection = Backbone.Collection.extend({
    model: FeatureFlagModel,
    load() {
        const deferred = $.Deferred();
        const fetches = this.map((flag) => flag.bootstrap());
        $.when.apply(null, fetches).done(() => {
            deferred.resolve();
        });
        return deferred;
    },
});

const FLAGS = {
    "Program and Campaign Management": {
        AUDIENCE_TAB: "AUDIENCE_TAB",
        ENHANCED_RULES: "ENHANCED_RULES",
        SHOW_ALL_VARIABLES: "SHOW_ALL_VARIABLES",
        RAF_V9: "RAF_V9",
    },
    "Developer Settings": {
        BETA_COMPONENT_LIBRARY: "BETA_COMPONENT_LIBRARY",
        ALLOW_EVALUATABLE_EDITING: "ALLOW_EVALUATABLE_EDITING",
        AI_SUPPORT: "AI_SUPPORT",
        FIXUPS_PAGE: "FIXUPS_PAGE",
    },
    "Program Types": {
        ADVOCATE_MARKETING_PACKAGE: "ADVOCATE_MARKETING_PACKAGE",
        DROP_A_HINT_PROGRAM: "DROP_A_HINT_PROGRAM",
        NOMINATION: "NOMINATION",
        SWEEPSTAKES: "SWEEPSTAKES",
        INFLUENCER_PROGRAM: "INFLUENCER_PROGRAM",
        FRIENDS_AND_FAMILY: "FRIENDS_AND_FAMILY",
        WELCOME_OFFER: "WELCOME_OFFER",
        OFFER: "OFFER",
    },
    Integrations: {
        ENFORCE_SHOPIFY_RESTRICTIONS: "ENFORCE_SHOPIFY_RESTRICTIONS",
    },
    "User Support": {
        HIDE_PII: "HIDE_PII",
        HIDE_COUPON_CODE: "HIDE_COUPON_CODE",
        HIDE_CART_VALUE: "HIDE_CART_VALUE",
    },
    Navigation: {
        SELF_SERVICE_PAGE: "SELF_SERVICE_PAGE",
        CHAT_BOT: "CHAT_BOT",
        INTERCOM: "INTERCOM",
        SELF_SERVICE_ROLE: "SELF_SERVICE_ROLE",
    },
};

const flags = new FeatureFlagCollection([
    {
        id: FLAGS["Program and Campaign Management"].RAF_V9,
        name: PREFIX + FLAGS["Program and Campaign Management"].RAF_V9,
        displayName: "Refer a Friend V9",
        description: "Enable the v9 refer a friend campaign for duplication",
    },
    {
        id: FLAGS["Program and Campaign Management"].AUDIENCE_TAB,
        name: PREFIX + FLAGS["Program and Campaign Management"].AUDIENCE_TAB,
        displayName: "Audience Tab in Campaign Editor",
        description:
            "Tab in Campaign Editor to specify targeting criteria and determine participant eligibility using Audiences and rules, will be available for V9 campaigns",
    },
    {
        id: FLAGS["Program and Campaign Management"].ENHANCED_RULES,
        name: PREFIX + FLAGS["Program and Campaign Management"].ENHANCED_RULES,
        displayName: "Enhanced Rules in Campaign Editor",
        description:
            'Enable viewing and editing of Custom Quality Rules and matching Reward Slots in the Rules tab of Campaign Editor. Matching Reward Slots ("These rules apply to all rewards received across the following campaigns") only available for legacy rewards/reward rules.',
    },
    {
        id: FLAGS["Program and Campaign Management"].SHOW_ALL_VARIABLES,
        name:
            PREFIX +
            FLAGS["Program and Campaign Management"].SHOW_ALL_VARIABLES,
        displayName: "Hidden Variables in Campaign Editor",
        description:
            'For V7 and below campaigns, show all variables in Campaign Editor, even if they\'re not visible to clients (for V8 campaigns, this is handled using the "Expert" importance level)',
    },
    {
        id: FLAGS["Developer Settings"].BETA_COMPONENT_LIBRARY,
        name: PREFIX + FLAGS["Developer Settings"].BETA_COMPONENT_LIBRARY,
        displayName: "Beta Component Library in Component Source Dropdowns",
        description:
            "Access the Beta Components Library, managed by Solutions, in the source dropdown when creating new components",
    },
    {
        id: FLAGS["Developer Settings"].ALLOW_EVALUATABLE_EDITING,
        name: PREFIX + FLAGS["Developer Settings"].ALLOW_EVALUATABLE_EDITING,
        displayName: "Evaluatable Editing for Component Fields",
        description:
            "Evaluatable editing for component fields across My.Extole",
    },
    {
        id: FLAGS["Developer Settings"].AI_SUPPORT,
        name: PREFIX + FLAGS["Developer Settings"].AI_SUPPORT,
        displayName: "AI Support",
        description:
            "AI-powered report descriptions and JavaScript conditions/actions in prehandlers",
    },
    {
        id: FLAGS["Developer Settings"].FIXUPS_PAGE,
        name: PREFIX + FLAGS["Developer Settings"].FIXUPS_PAGE,
        displayName: "Fixups Page in Tech Center",
        description:
            "Access Fixup Logs from the Tech Center to see consumer, reward, and message events that have been altered in order to fix historical data",
    },
    {
        id: FLAGS["Program Types"].ADVOCATE_MARKETING_PACKAGE,
        name: PREFIX + FLAGS["Program Types"].ADVOCATE_MARKETING_PACKAGE,
        displayName: "Advocate Marketing Package",
        description:
            "Unlock the following program types in the Program Picker: Influencer, FAF, and Nomination.",
    },
    {
        id: FLAGS["Program Types"].DROP_A_HINT_PROGRAM,
        name: PREFIX + FLAGS["Program Types"].DROP_A_HINT_PROGRAM,
        displayName: "Drop A Hint Program",
        description: "Unlock Drop A Hint in the Program Picker",
    },
    {
        id: FLAGS["Program Types"].NOMINATION,
        name: PREFIX + FLAGS["Program Types"].NOMINATION,
        displayName: "Nomination Program",
        description: "Unlock Nomination in the Program Picker",
    },
    {
        id: FLAGS["Program Types"].SWEEPSTAKES,
        name: PREFIX + FLAGS["Program Types"].SWEEPSTAKES,
        displayName: "Sweepstakes Program",
        description: "Unlock Sweepstakes in the Program Picker",
    },
    {
        id: FLAGS["Program Types"].INFLUENCER_PROGRAM,
        name: PREFIX + FLAGS["Program Types"].INFLUENCER_PROGRAM,
        displayName: "Influencer Program",
        description: "Unlock Influencer in the Program Picker",
    },
    {
        id: FLAGS["Program Types"].FRIENDS_AND_FAMILY,
        name: PREFIX + FLAGS["Program Types"].FRIENDS_AND_FAMILY,
        displayName: "Friends & Family Program",
        description: "Unlock Friends and Family in the Program Picker",
    },
    {
        id: FLAGS["Program Types"].WELCOME_OFFER,
        name: PREFIX + FLAGS["Program Types"].WELCOME_OFFER,
        displayName: "Welcome Offer Program",
        description: "Unlock Welcome Offer in the Program Picker",
    },
    {
        id: FLAGS["Program Types"].OFFER,
        name: PREFIX + FLAGS["Program Types"].OFFER,
        displayName: "Offer Program",
        description: "Unlock Offer in the Program Picker",
    },
    {
        id: FLAGS["Integrations"].ENFORCE_SHOPIFY_RESTRICTIONS,
        name: PREFIX + FLAGS["Integrations"].ENFORCE_SHOPIFY_RESTRICTIONS,
        displayName: "Enforce Shopify Restrictions",
        description:
            "When the Shopify integration is enabled, only show reward supplier options and program types that are available for Shopify customers",
    },
    {
        id: FLAGS["User Support"].HIDE_PII,
        name: PREFIX + FLAGS["User Support"].HIDE_PII,
        displayName: "Hide Personally Identifiable Information",
        description:
            "Hide customer profile pictures from Support and the Share Messages report from the account-level dashboard",
    },
    {
        id: FLAGS["User Support"].HIDE_COUPON_CODE,
        name: PREFIX + FLAGS["User Support"].HIDE_COUPON_CODE,
        displayName: "Disable Coupon Codes in Support",
        description: `Disable coupon code from being shown as a top-level parameter in View Event pages, and hide the "Upgrade Your Tag" link to Dev Docs if coupon code is not present`,
    },
    {
        id: FLAGS["User Support"].HIDE_CART_VALUE,
        name: PREFIX + FLAGS["User Support"].HIDE_CART_VALUE,
        displayName: "Disable Cart Value in Support",
        description: `Disable cart value from being shown as a top-level parameter in View Event pages, and hide the "Upgrade Your Tag" link to Dev Docs if cart value is not present`,
    },
    {
        id: FLAGS["Navigation"].SELF_SERVICE_PAGE,
        name: PREFIX + FLAGS["Navigation"].SELF_SERVICE_PAGE,
        displayName: "Self Service Page Menu Item",
        description:
            "Access custom self service pages from a left navigation menu item",
    },
    {
        id: FLAGS["Navigation"].CHAT_BOT,
        name: PREFIX + FLAGS["Navigation"].CHAT_BOT,
        displayName: "Chat Bot",
        description: "See the customer support chat widget",
    },
    {
        id: FLAGS["Navigation"].INTERCOM,
        name: PREFIX + FLAGS["Navigation"].INTERCOM,
        displayName: "Intercom Chat Bot",
        description: "See the customer support Intercom chat widget",
    },
    {
        id: FLAGS["Navigation"].SELF_SERVICE_ROLE,
        name: PREFIX + FLAGS["Navigation"].SELF_SERVICE_ROLE,
        displayName: "Self Service Role",
        description: "Enable self service role for the user",
    },
]);

const service = {
    FLAGS,

    isOn(featureFlagId) {
        const flag = flags.findWhere({
            id: featureFlagId,
        });

        if (!flag) {
            return false;
        }

        return flag.isOn();
    },

    loadFlags() {
        const deferred = $.Deferred();
        flags.load().done(() => {
            deferred.resolve(flags);
        });
        return deferred;
    },
};

export default service;
